import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Sparkles, Clock, Activity, AlertTriangle, Users as UsersIcon } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import HowForsytRollsIn from '../components/HowForsytRollsIn';

const STACK_LOGOS = [
  { name: 'Salesforce', src: null, color: '#00A1E0' },
  { name: 'Zoho', src: null, color: '#E42527' },
  { name: 'Gong', src: '/assets/dcxyztic.png' },
  { name: 'Gmail', src: '/assets/0hye7w5y.png' },
  { name: 'Slack', src: '/assets/yi9s0mbf.webp' },
  { name: 'Jira', src: '/assets/sbre7usi.png' },
];

const IMG_RISK_RADAR = '/product-risk-radar.webp';
const IMG_OPPS = '/product-opportunities.webp';
const IMG_FOCUS = '/product-mail-intelligence.webp';

const rolesData = {
  cro: {
    title: 'Chief Revenue Officer',
    shortTitle: 'CRO',
    headline: 'See the pipeline as it actually is. Act before deals slip.',
    subheadline: 'Forsyt gives CROs a single pane of truth across the entire revenue engine, no more spreadsheet backchannels, no more forecast interrogation calls.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: '30-50%', label: 'less time spent on forecast interrogation' },
      { value: 'Daily', label: 'pipeline health re-scoring, not quarterly' },
      { value: '1 view', label: 'replaces board decks, spreadsheets, and 1:1 asks' },
      { value: 'Real-time', label: 'risk alerts across the entire org' },
    ],
    painPoints: [
      'Pipeline reviews based on gut feel, not data',
      'Spending 5+ hours/week chasing reps for deal updates',
      'Deals slipping silently between quarterly reviews',
      'No visibility into rep activity vs. pipeline health',
    ],
    capabilities: [
      { title: 'Risk Radar', desc: 'See which deals are stalling in real-time with automated health scoring. No more surprises at QBR.' },
      { title: 'Pipeline Intelligence', desc: 'AI-scored pipeline with deal-by-deal risk signals, so you know exactly where to focus your coaching time.' },
      { title: 'Team Performance', desc: 'Quota attainment, activity metrics, and rep-level pipeline health, all in one view.' },
      { title: 'Action Prescriptions', desc: 'Stop asking "what happened?" Start asking "what should we do next?" Forsyt tells you.' },
    ],
    dayInLife: {
      title: 'A typical Tuesday for a CRO on Forsyt',
      steps: [
        { time: '8:00 AM', text: 'Open the CRO dashboard, quarter pipeline coverage, risk-tier breakdown, and the top 5 at-risk deals are one glance away.' },
        { time: '9:30 AM', text: 'AI Deal Chat: ask "which deals slipped overnight?", get an instant briefing with reasons and owners.' },
        { time: '11:00 AM', text: 'Forecast call with VP Sales. No exports needed, open a shared Forsyt view and walk through team health together.' },
        { time: '2:00 PM', text: 'Board prep: ask Forsyt to summarize top 10 deals, blockers, and recommended interventions. Ship it straight to Slides.' },
        { time: '5:30 PM', text: 'Notification: 2 strategic accounts hit At-Risk. Delegate the save plays to managers with one click.' },
      ],
    },
    faq: [
      { q: 'How long until I see value as a CRO?', a: 'Most CROs see a cleaner pipeline view within the first week. Meaningful behavioural change across the team typically shows up in 30, 60 days.' },
      { q: 'Does Forsyt replace my CRM?', a: 'No. Forsyt sits on top of your CRM and writes back to it. Salesforce / Zoho / LeadSquared remain the system of record.' },
      { q: 'How do I justify ROI to the CFO?', a: 'Forsyt tracks prescription-to-outcome correlation and quantifies hours saved per rep. We guarantee measurable ROI or you don\'t pay.' },
    ],
    cta: 'See the CRO Dashboard',
    ctaLink: '/dashboard',
    related: ['revops', 'sales-manager'],
    quote: 'I like the concept and actions. The core thought is right, the dashboard should show pipeline health based on stages and the actions depending on them.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  revops: {
    title: 'Revenue Operations',
    shortTitle: 'RevOps',
    headline: 'Clean data. Clear process. Zero manual hygiene.',
    subheadline: 'Forsyt automates the CRM hygiene you spend hours enforcing and surfaces process gaps before they become forecast misses.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '2.5 hrs/day', label: 'saved per rep, no more chasing for CRM updates' },
      { value: '100%', label: 'auditable trail of every field change' },
      { value: 'Auto', label: 'stage gate enforcement across the team' },
      { value: 'Zero', label: 'manual data reconciliation between tools' },
    ],
    painPoints: [
      'Reps not updating CRM fields correctly or on time',
      'Manual data pulls for every pipeline review',
      'No standardized process adherence across the team',
      'Hours spent reconciling data across tools',
    ],
    capabilities: [
      { title: 'CRM Auto-Sync', desc: 'Fields update automatically from calls, emails, and Slack. No more chasing reps for data entry.' },
      { title: 'Process Compliance', desc: 'See which reps are following the sales process and where deals are skipping critical stages.' },
      { title: 'Pipeline Health Scoring', desc: 'Automated scoring based on real signals, not rep self-assessments.' },
      { title: 'Smart Notifications', desc: 'Get alerted when deals deviate from healthy patterns before they become problems.' },
    ],
    dayInLife: {
      title: 'Month-end without the fire drill',
      steps: [
        { time: 'Week 1', text: 'Process compliance dashboard surfaces reps skipping Stage 3. Coaching starts before month-end.' },
        { time: 'Week 2', text: 'CRM hygiene scores trend green, fields auto-populated from calls and emails.' },
        { time: 'Week 3', text: 'Forecast call: no last-minute data pulls. Forsyt already has every update logged.' },
        { time: 'Month-end', text: 'Close the books in hours, not days. Every stage gate audit-ready with source attribution.' },
      ],
    },
    faq: [
      { q: 'Do we need to migrate off Salesforce?', a: 'No. Forsyt reads and writes to your existing CRM. Zero migration.' },
      { q: 'How do custom fields map in?', a: 'Point-and-click mapping interface. RevOps admins can configure without engineering help.' },
      { q: 'Is auto-update reversible?', a: 'Every change is logged with source. Reps review before finalizing, and changes can be reverted with one click.' },
    ],
    cta: 'See RevOps in Action',
    ctaLink: '/dashboard',
    related: ['cro', 'sales-manager'],
    quote: 'The biggest challenge in revenue isn\'t knowing what to do, it\'s getting reps to consistently do it.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
  'sales-manager': {
    title: 'Sales Manager',
    shortTitle: 'Manager',
    headline: 'Coach deals, not spreadsheets.',
    subheadline: 'Forsyt tells you which deals need your attention, what the blocker is, and what your rep should do next, so your 1:1s become action-oriented.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: '4x', label: 'faster to identify who needs coaching today' },
      { value: '0 prep', label: '1:1s, pull up the rep\'s deals in-line' },
      { value: 'Signal', label: 'not stage: coach on real deal health' },
      { value: 'Daily', label: 'risk alerts, catch slippage early' },
    ],
    painPoints: [
      'Pipeline reviews that surface problems too late',
      'Reps saying "it\'s fine" when deals are stuck',
      'No systematic way to prioritize coaching time',
      'Manual effort to prep for deal reviews',
    ],
    capabilities: [
      { title: 'Deal Risk Signals', desc: 'See exactly which deals are at risk and why, multi-threaded? Stalled? Missing next steps?' },
      { title: 'Rep Activity Tracking', desc: 'Understand how each rep is spending their time vs. how they should be, without micromanaging.' },
      { title: 'Action Prescriptions', desc: 'AI-recommended next steps for every deal, so you can coach reps on what to do, not just what went wrong.' },
      { title: 'Team Leaderboard', desc: 'Quota attainment, activities, and pipeline metrics side by side for your entire team.' },
    ],
    dayInLife: {
      title: 'A manager\'s Monday morning, rebuilt',
      steps: [
        { time: '8:30 AM', text: 'Team Health Digest: 2 reps trending below quota, 3 at-risk deals overnight.' },
        { time: '9:00 AM', text: 'Standup: not "how are your deals?" but "Sarah, let\'s multi-thread on Acme."' },
        { time: '10:30 AM', text: '1:1 with a struggling rep. Pull up their pipeline with risk overlay, the conversation stays specific.' },
        { time: '3:00 PM', text: 'Manager assigns save plays to 4 reps from their dashboard with one click each.' },
      ],
    },
    faq: [
      { q: 'Will reps feel surveilled?', a: 'Forsyt is built to empower reps, not grade them. Reps see the same data their manager sees, transparency by default.' },
      { q: 'How does it change my 1:1s?', a: 'Instead of "pipeline review" theatre, 1:1s become action-oriented coaching. Managers report 40%+ shorter 1:1s with more decisions made.' },
      { q: 'Can I define my own playbook?', a: 'Yes, upload your sales methodology and Forsyt prescribes actions consistent with it.' },
    ],
    cta: 'See the Manager View',
    ctaLink: '/dashboard',
    related: ['sales-reps', 'cro'],
    quote: 'Every revenue leader knows that deals slip because of a lack of follow-through, not a lack of forecasting data.',
    quoteAuthor: 'Rajath KN',
    quoteRole: 'Associate Director, Ex-IBM / Ex-Whatfix',
  },
  'sales-reps': {
    title: 'Sales Reps',
    shortTitle: 'Reps',
    headline: 'Less admin. More selling. One card per day.',
    subheadline: 'Forsyt shows you the one deal that matters most today, tells you exactly what to do, and auto-updates your CRM, so you can stay in flow.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '2.5 hrs/day', label: 'reclaimed from CRM admin and tool-hopping' },
      { value: '1 card', label: 'per day, zero decision fatigue' },
      { value: 'Auto', label: 'logged calls, emails, meetings' },
      { value: 'Drafted', label: 'follow-up emails ready to send in 1 click' },
    ],
    painPoints: [
      'Spending 2+ hours/day on CRM data entry',
      'Not knowing which deal to prioritize',
      'Losing context between calls, emails, and Slack threads',
      'Guessing at next steps instead of following a prescribed play',
    ],
    capabilities: [
      { title: "Today's Focus", desc: 'One screen, one priority. See your highest-impact action, overdue follow-ups, and daily task list.' },
      { title: 'AI Deal Chat', desc: 'Ask "What\'s blocking the Acme deal?" and get an instant answer with context from calls, emails, and CRM.' },
      { title: 'Auto CRM Updates', desc: 'Forsyt writes to Salesforce, Zoho, or LeadSquared fields automatically. No more manual entry.' },
      { title: 'Action-Driven Workflows', desc: 'Stop guessing. Forsyt tells you the exact next step based on your deal\'s current stage and signals.' },
    ],
    dayInLife: {
      title: 'A rep\'s day on Forsyt',
      steps: [
        { time: '8:45 AM', text: 'Open laptop. One card: "Multi-thread Acme to VP Engineering today, champion went quiet."' },
        { time: '9:00 AM', text: 'Hit "Do it for me", Forsyt drafts the email with the exact case study that worked for Globex.' },
        { time: '11:30 AM', text: 'Discovery call ends. Stage, next steps, close date updated automatically, rep approves in 10 seconds.' },
        { time: '3:15 PM', text: 'Alert: Acme champion opened your deck 3 times in the last hour, send pricing now.' },
        { time: '5:30 PM', text: 'Daily review shows: 4 actions completed, 2 deals moved forward, 0 CRM busywork.' },
      ],
    },
    faq: [
      { q: 'Is this another tool I have to open?', a: 'Forsyt meets you in Slack and email. You only open the app when you want to deep-dive.' },
      { q: 'What if I disagree with the priority?', a: 'You can dismiss, re-order, or swap in your own priority. Forsyt learns from your choices.' },
      { q: 'Will my manager see everything I do?', a: 'Activity is shared at the same level you\'d already see in Salesforce. No hidden surveillance, by design.' },
    ],
    cta: 'Try the Rep Dashboard',
    ctaLink: '/dashboard/todays-focus',
    related: ['sales-manager', 'sdr-bdr'],
    quote: 'This is what was needed, it will definitely help the sales team.',
    quoteAuthor: 'Sandeep',
    quoteRole: 'CRO',
  },
  'sdr-bdr': {
    title: 'SDR / BDR',
    shortTitle: 'SDR/BDR',
    headline: 'Hit your meetings target without the guesswork.',
    subheadline: 'Forsyt prioritizes your outreach list, tracks your cadences, and tells you which prospects to call next, based on real engagement signals.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: 'Intent-sorted', label: 'prospect list, the hottest leads surface first' },
      { value: 'Auto', label: 'logged calls, emails, and LinkedIn touches' },
      { value: 'Real-time', label: 'meeting-booked vs. target tracker' },
      { value: 'Clean', label: 'handoff context transferred to AEs automatically' },
    ],
    painPoints: [
      'Spending time on low-intent prospects',
      'No visibility into which outreach sequence is working',
      'Manual logging of activities in CRM',
      'Inconsistent handoff quality to AEs',
    ],
    capabilities: [
      { title: 'Prioritized Outreach', desc: 'See your prospects ranked by engagement signals and intent, so you focus on the ones most likely to convert.' },
      { title: 'Activity Auto-Logging', desc: 'Calls, emails, and LinkedIn touches logged automatically. Spend time selling, not typing.' },
      { title: 'Meeting Booking Tracker', desc: 'Track your meetings booked vs. target in real-time with daily and weekly views.' },
      { title: 'Handoff Intelligence', desc: 'When you pass a lead to an AE, Forsyt packages the full context, no more lost information.' },
    ],
    dayInLife: {
      title: 'SDR prospecting, intent-first',
      steps: [
        { time: '9:00 AM', text: 'Dialer list is pre-sorted: 10 prospects who engaged with last week\'s campaign are on top.' },
        { time: '11:30 AM', text: '4 calls completed, 2 booked. Every touch auto-logged, no CRM hand-holding.' },
        { time: '2:00 PM', text: 'Manager sees a live board: meetings booked vs. target, cadence health, activity mix.' },
        { time: '5:00 PM', text: 'Handoff to AE: Forsyt packages the entire interaction history, call transcripts, emails, pain points.' },
      ],
    },
    faq: [
      { q: 'Does it replace Outreach or Salesloft?', a: 'No. Forsyt integrates with your sequencer to add intent-based prioritization and automatic logging.' },
      { q: 'Can I see cadence effectiveness?', a: 'Yes, sequence-level conversion analytics show which templates and cadences produce meetings vs. just opens.' },
      { q: 'What intent signals are used?', a: 'Product-fit fit (CRM data) + website visits + campaign engagement + social signals, synthesized into a single score.' },
    ],
    cta: 'See the SDR Dashboard',
    ctaLink: '/dashboard/todays-focus',
    related: ['sales-reps', 'marketing'],
    quote: 'The real-time nudges can completely change a sales team\'s follow-through.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
  'cs-leader': {
    title: 'Customer Success Leader',
    shortTitle: 'CS Leader',
    headline: 'Detect churn early. Act decisively.',
    subheadline: 'Forsyt monitors customer health signals across every touchpoint and alerts your team before renewal risk becomes real churn.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: '30-90 days', label: 'of early warning before renewal risk crystallizes' },
      { value: 'Multi-signal', label: 'usage + tickets + sentiment + meetings' },
      { value: 'Renewal', label: 'pipeline visualized like a sales pipeline' },
      { value: 'Expansion', label: 'signals surface ready-to-upsell accounts' },
    ],
    painPoints: [
      'Churn signals hidden across support tickets, calls, and NPS',
      'No proactive playbook, reacting after the damage is done',
      'CS team bandwidth spread too thin across accounts',
      'Renewals at risk with no systematic early warning',
    ],
    capabilities: [
      { title: 'Customer Health Scoring', desc: 'Automated health scores based on product usage, support tickets, sentiment, and engagement patterns.' },
      { title: 'Churn Risk Alerts', desc: 'Get notified the moment an account shows early warning signs, not when they send the cancellation email.' },
      { title: 'Renewal Pipeline', desc: 'See upcoming renewals with risk scoring and recommended actions for each account.' },
      { title: 'Expansion Signals', desc: 'Identify accounts ready for upsell based on usage growth and positive sentiment trends.' },
    ],
    dayInLife: {
      title: 'Running a post-sale org on leading indicators',
      steps: [
        { time: 'Month -3', text: 'Early warning: top-10 account\'s usage dropped 40%. Alert to the CSM with a recommended QBR request.' },
        { time: 'Month -2', text: 'Executive sponsor change detected on the customer side. Play: warm intro meeting in 2 weeks.' },
        { time: 'Month -1', text: 'Renewal proposal generated with usage-driven ROI numbers pre-populated.' },
        { time: 'Renewal day', text: 'Signed, expanded, on time, no last-minute scramble.' },
      ],
    },
    faq: [
      { q: 'Which customer data sources are supported?', a: 'Product telemetry (Segment, Amplitude, Mixpanel), support (Zendesk, Freshdesk, Intercom), CRM, and direct API integration.' },
      { q: 'Can I define my own health score?', a: 'Yes, weight signals by product line, segment, or ARR band. No data science team required.' },
      { q: 'Does it identify expansion opportunities?', a: 'Yes, accounts with rising usage and positive sentiment are flagged as expansion-ready with a suggested play.' },
    ],
    cta: 'See the CS Dashboard',
    ctaLink: '/dashboard',
    related: ['cs-manager', 'cro'],
    quote: 'Building a platform whose sole purpose is to drive critical actions is a massive step forward.',
    quoteAuthor: 'Rajath KN',
    quoteRole: 'Associate Director, Ex-IBM / Ex-Whatfix',
  },
  'cs-manager': {
    title: 'Customer Success Manager',
    shortTitle: 'CSM',
    headline: 'Know your book of business. Act on what matters.',
    subheadline: 'Forsyt gives every CSM a daily action list based on real customer signals, not just calendar reminders.',
    previewImage: IMG_FOCUS,
    metrics: [
      { value: '50+', label: 'accounts manageable per CSM with real prioritization' },
      { value: 'Smart', label: 'check-in timing based on health, not calendar' },
      { value: 'Aggregate', label: 'sentiment across calls, email, tickets' },
      { value: 'Every', label: 'touchpoint on one engagement timeline' },
    ],
    painPoints: [
      'Managing 50+ accounts with no prioritization framework',
      'Relying on memory and calendar for check-ins',
      'Missing subtle signals that indicate churn risk',
      'Spending more time on admin than customer conversations',
    ],
    capabilities: [
      { title: 'Daily Account Actions', desc: 'Wake up to a prioritized list of which accounts need attention today and why.' },
      { title: 'Sentiment Tracking', desc: 'Aggregate sentiment signals from calls, emails, and support tickets per account.' },
      { title: 'Engagement Timeline', desc: 'Full history of every customer touchpoint across your team, calls, emails, tickets, and product usage.' },
      { title: 'Automated Check-In Reminders', desc: 'Smart reminders based on account health, not arbitrary timelines.' },
    ],
    dayInLife: {
      title: 'A CSM covering 60+ accounts',
      steps: [
        { time: '9:00 AM', text: 'Daily action card: 3 accounts need attention today, ranked by ARR at risk.' },
        { time: '10:30 AM', text: 'Quick check-in with the top account, context already loaded on the engagement timeline.' },
        { time: '2:00 PM', text: 'Ticket volume spike detected on a mid-market account, AI drafts a proactive executive outreach.' },
        { time: '4:30 PM', text: 'End-of-day update: 3 saves confirmed, 2 expansion conversations scheduled.' },
      ],
    },
    faq: [
      { q: 'How does it integrate with our CS tool?', a: 'Forsyt complements Gainsight/Totango by adding action-oriented daily prioritization and auto-logging.' },
      { q: 'Does it handle low-touch accounts?', a: 'Yes. Low-touch segments get automated check-in emails and digital playbooks driven by usage signals.' },
      { q: 'Can I see a single customer\'s full story?', a: 'Yes, the engagement timeline aggregates every call, email, ticket, and product event chronologically.' },
    ],
    cta: 'See the CSM View',
    ctaLink: '/dashboard/todays-focus',
    related: ['cs-leader', 'sales-reps'],
    quote: 'We have enough dashboards telling us what went wrong. Forsyt steps in to fix behaviour before deals are lost.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  enablement: {
    title: 'Sales Enablement',
    shortTitle: 'Enablement',
    headline: 'Measure what reps actually do, not just what they know.',
    subheadline: 'Forsyt bridges the gap between enablement programs and field execution, showing you which playbooks are being followed and which are being ignored.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: 'Deal-level', label: 'adoption tracking for MEDDIC / BANT / custom' },
      { value: '50%', label: 'faster rep ramp time reported by pilots' },
      { value: 'Outcome-tied', label: 'enablement ROI measured against revenue' },
      { value: 'Live', label: 'coaching flag, spot the struggle in real time' },
    ],
    painPoints: [
      'No visibility into whether training is changing behaviour',
      'Playbooks created but not followed consistently',
      'Can\'t connect enablement efforts to revenue outcomes',
      'Reps revert to old habits within weeks of training',
    ],
    capabilities: [
      { title: 'Process Adherence Tracking', desc: 'See which reps are following the prescribed sales process and where they deviate.' },
      { title: 'Playbook Compliance', desc: 'Track adoption of your methodologies, MEDDIC, BANT, or custom frameworks, deal by deal.' },
      { title: 'Coaching Insights', desc: 'Surface patterns in where reps struggle so you can target enablement programs effectively.' },
      { title: 'Onboarding Acceleration', desc: 'New reps ramp faster when Forsyt prescribes the exact next step for every deal.' },
    ],
    dayInLife: {
      title: 'Proving enablement ROI',
      steps: [
        { time: 'Week 1', text: 'New cohort of reps onboarded, Forsyt prescribes the exact discovery playbook per deal.' },
        { time: 'Month 1', text: 'Adoption dashboard shows 78% process compliance vs. 35% pre-Forsyt.' },
        { time: 'Month 3', text: 'Correlation: reps following MEDDIC steps 1-4 close 2.1x more often.' },
        { time: 'QBR', text: 'Enablement presents real behavioural change tied to real revenue, not just training completion rates.' },
      ],
    },
    faq: [
      { q: 'Can I plug my existing content library into Forsyt?', a: 'Yes. Link Highspot, Seismic, or Notion content so playbook steps surface in context for reps.' },
      { q: 'How do I measure training impact?', a: 'Before/after cohort comparison on deal-level behaviours tied to outcomes like close rate and ACV.' },
      { q: 'Is it too rigid for high-context sales?', a: 'Playbook steps are suggestions, not mandates. Reps can deviate, but the system learns what works and refines.' },
    ],
    cta: 'See Enablement Insights',
    ctaLink: '/dashboard/teams',
    related: ['sales-manager', 'revops'],
    quote: 'The biggest challenge isn\'t knowing what to do, it\'s getting reps to consistently do it.',
    quoteAuthor: 'Aditya Ghosh',
    quoteRole: 'Lead Product Manager, Techjockey',
  },
  marketing: {
    title: 'Marketing',
    shortTitle: 'Marketing',
    headline: 'Close the loop between pipeline and campaigns.',
    subheadline: 'Forsyt shows Marketing which campaigns are driving pipeline that actually closes, and which deals from your leads are stalling.',
    previewImage: IMG_RISK_RADAR,
    metrics: [
      { value: 'Every', label: 'MQL tracked through to closed-won or lost' },
      { value: 'Real-time', label: 'stalled-deal alerts for ABM re-engagement' },
      { value: 'Attribution', label: 'tied to revenue, not just MQL counts' },
      { value: 'Aligned', label: 'Sales + Marketing on the same pipeline view' },
    ],
    painPoints: [
      'No visibility into what happens after MQL handoff',
      'Pipeline attribution is a quarterly forensic exercise',
      'Sales blames Marketing for lead quality with no data',
      'Can\'t see which campaign sources produce deals that close',
    ],
    capabilities: [
      { title: 'Pipeline Source Tracking', desc: 'See which lead sources and campaigns are generating deals that move through the pipeline.' },
      { title: 'Lead-to-Revenue Visibility', desc: 'Track every MQL through to closed-won or lost, with full context on what happened in between.' },
      { title: 'Stalled Deal Alerts', desc: 'Get notified when deals from your campaigns are stalling so you can coordinate with Sales on re-engagement.' },
      { title: 'Revenue Attribution', desc: 'Connect campaign spend to actual revenue outcomes, not just MQL counts.' },
    ],
    dayInLife: {
      title: 'Ending the Sales vs. Marketing blame game',
      steps: [
        { time: 'Week 1', text: 'Campaign launches, Forsyt tags every MQL with source and tracks downstream movement.' },
        { time: 'Week 4', text: 'Alert: 12 MQLs from a paid LinkedIn campaign stalled at Stage 2. Marketing reactivates with nurture.' },
        { time: 'QBR', text: 'Revenue attribution: the top-performing campaign drove $840K in closed ARR, not the one with most MQLs.' },
        { time: 'Next Q', text: 'Budget re-allocated to what actually drives revenue, with full Sales team buy-in.' },
      ],
    },
    faq: [
      { q: 'Which MarTech tools integrate?', a: 'Native HubSpot, Marketo, Pardot, and Google Ads. Zapier + API fill remaining gaps.' },
      { q: 'How is attribution calculated?', a: 'Multi-touch by default, first-touch, last-touch, and custom models available. Models transparently show contribution weights.' },
      { q: 'Can Sales see campaign context?', a: 'Yes, reps see the campaign that sourced each lead inside the account page, so Sales + Marketing context stays connected.' },
    ],
    cta: 'See Marketing Insights',
    ctaLink: '/dashboard/risk-radar',
    related: ['sdr-bdr', 'revops'],
    quote: 'The core thought is right, show pipeline health based on stages and the actions depending on them.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
  product: {
    title: 'Product Teams',
    shortTitle: 'Product',
    headline: 'Hear the voice of the customer, from revenue data.',
    subheadline: 'Forsyt surfaces product feedback, feature requests, and competitive mentions buried in sales conversations, so Product teams build what customers actually need.',
    previewImage: IMG_OPPS,
    metrics: [
      { value: 'Every call', label: 'mined for feature requests + objections' },
      { value: 'Ranked', label: 'by revenue impact, not just mention count' },
      { value: 'Auto', label: 'competitive intelligence on every deal' },
      { value: 'Win/loss', label: 'analysis synthesized from full journey' },
    ],
    painPoints: [
      'Feature requests lost in Slack threads and call notes',
      'No systematic way to capture competitive intelligence from sales',
      'Disconnect between what customers say and what Product builds',
      'Relying on Sales anecdotes instead of aggregated data',
    ],
    capabilities: [
      { title: 'Customer Feedback Aggregation', desc: 'Automatically capture product feedback from sales calls and customer conversations.' },
      { title: 'Competitive Intelligence', desc: 'See which competitors come up in deals and what prospects say about them.' },
      { title: 'Feature Request Tracking', desc: 'Surface the most-requested features from real customer conversations, ranked by revenue impact.' },
      { title: 'Win/Loss Analysis', desc: 'Understand why deals are won or lost with data from the entire customer journey.' },
    ],
    dayInLife: {
      title: 'Product planning backed by revenue data',
      steps: [
        { time: 'Weekly', text: 'Digest: top 5 feature requests, each weighted by the ARR of accounts that asked for it.' },
        { time: 'Quarterly', text: 'Competitive battlecards update automatically based on what prospects actually say on calls.' },
        { time: 'Win/Loss', text: '100% loss deals analysed with AI summarization, no more one-off interviews.' },
        { time: 'Roadmap', text: 'Priorities defended with data: "this feature maps to $2.4M in open pipeline."' },
      ],
    },
    faq: [
      { q: 'Does Product see rep-level data?', a: 'Only aggregated signals, not individual rep performance. Signal-based insights only.' },
      { q: 'How are feature requests deduped?', a: 'AI clusters similar requests and maintains a canonical list, with source-backed evidence.' },
      { q: 'Can Product leaders export this data?', a: 'Yes, one-click export to Linear, Jira, or Productboard.' },
    ],
    cta: 'See Product Insights',
    ctaLink: '/dashboard',
    related: ['marketing', 'cs-leader'],
    quote: 'It bridges the gap between data and execution.',
    quoteAuthor: 'Chaitanya Kakkar',
    quoteRole: 'VP - Business & International Expansion, SingleInterface',
  },
};

/* ═══════ SUB-COMPONENTS ═══════ */
function LogoChip({ logo }) {
  if (logo.src) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
          <img src={logo.src} alt={logo.name} className="w-7 h-7 object-contain" />
        </div>
        <span className="text-xs font-medium text-gray-600">{logo.name}</span>
      </div>
    );
  }
  if (logo.name === 'Salesforce') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24">
            <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.176 5.22c-.345 0-.69-.044-1.02-.104a3.75 3.75 0 01-3.3 1.95c-.6 0-1.155-.15-1.65-.375A4.314 4.314 0 018.88 20.4a4.302 4.302 0 01-4.05-2.82c-.27.062-.54.076-.825.076-2.204 0-4.005-1.8-4.005-4.05 0-1.5.811-2.805 2.01-3.51-.255-.57-.39-1.2-.39-1.846 0-2.58 2.1-4.65 4.65-4.65 1.53 0 2.85.705 3.72 1.8"/>
          </svg>
        </div>
        <span className="text-xs font-medium text-gray-600">Salesforce</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center">
        <span className="font-bold text-base" style={{ color: logo.color }}>{logo.name.charAt(0)}</span>
      </div>
      <span className="text-xs font-medium text-gray-600">{logo.name}</span>
    </div>
  );
}

function RelatedRoleCard({ roleSlug }) {
  const rel = rolesData[roleSlug];
  if (!rel) return null;
  return (
    <Link
      to={`/solutions/${roleSlug}`}
      className="group block p-5 rounded-2xl border border-gray-200 bg-white hover:border-blue-300 hover:shadow-md transition-all"
      data-testid={`related-role-${roleSlug}`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
          <UsersIcon className="w-4 h-4 text-blue-600" />
        </div>
        <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-700">{rel.title}</h4>
      </div>
      <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{rel.headline}</p>
      <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-blue-600">
        Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </Link>
  );
}

export default function Solutions() {
  const { role } = useParams();
  const navigate = useNavigate();
  const data = rolesData[role];

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Solution not found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Back to home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title={`Forsyt for ${data.title}, Revenue OS`}
        description={data.subheadline}
        path={`/solutions/${role}`}
      />

      {/* Hero */}
      <section className="pt-20 pb-10 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5" data-testid="solution-role-tag">
            <UsersIcon className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">Forsyt for {data.title}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight" data-testid="solution-headline">
            {data.headline}
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto" data-testid="solution-subheadline">
            {data.subheadline}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate(data.ctaLink)}
              className="inline-flex items-center px-7 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all"
              data-testid="solution-cta-primary"
            >
              {data.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3 text-sm font-semibold border border-gray-300 hover:border-blue-600 bg-white rounded-full transition-all"
              data-testid="solution-cta-secondary"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Dashboard preview */}
      {data.previewImage && (
        <section className="pb-12 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-5xl mx-auto px-6">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
              <img
                src={data.previewImage}
                alt={`Forsyt ${data.title} dashboard preview`}
                className="w-full h-auto block"
                loading="lazy"
              />
              <div className="absolute top-3 left-3 flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Metrics */}
      {data.metrics && (
        <section className="py-10 bg-gray-50 border-y border-gray-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {data.metrics.map((m, i) => (
                <div key={i} className="text-center" data-testid={`sol-metric-${i}`}>
                  <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                    {m.value}
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 leading-snug">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pain Points */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-xs font-bold text-red-700 uppercase tracking-widest mb-3">
              <AlertTriangle className="w-3 h-3" /> Today's reality
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
              Challenges {data.title} teams face today
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {data.painPoints.map((pain, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-red-50/60 border border-red-100">
                <span className="text-red-500 font-bold text-lg mt-0.5 leading-none">, </span>
                <p className="text-sm text-gray-800 leading-snug">{pain}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-xs font-bold text-blue-700 uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> How Forsyt helps
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Purpose-built for {data.shortTitle}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {data.capabilities.map((cap, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all" data-testid={`capability-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 shrink-0" />
                  <h3 className="text-base md:text-lg font-bold text-gray-900">{cap.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Day in life */}
      {data.dayInLife && (
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">
                <Clock className="w-3 h-3" /> A day in the life
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                {data.dayInLife.title}
              </h2>
            </div>
            <div className="relative pl-6 md:pl-8 space-y-6 border-l-2 border-dashed border-blue-200">
              {data.dayInLife.steps.map((step, i) => (
                <div key={i} className="relative" data-testid={`sol-example-${i}`}>
                  <div className="absolute -left-[35px] md:-left-[41px] top-1 w-6 h-6 rounded-full bg-blue-600 ring-4 ring-white flex items-center justify-center">
                    <Activity className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-5">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest whitespace-nowrap md:min-w-[90px]">{step.time}</span>
                    <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stack */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-6">Works with your existing stack</p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {STACK_LOGOS.map((logo) => (
              <LogoChip key={logo.name} logo={logo} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {data.faq && (
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-xs font-bold text-purple-700 uppercase tracking-widest mb-3">
                <AlertTriangle className="w-3 h-3" /> FAQ
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                Common questions from {data.shortTitle} teams
              </h2>
            </div>
            <div className="space-y-3">
              {data.faq.map((item, i) => (
                <details key={i} className="group bg-gray-50 rounded-xl border border-gray-200 open:border-blue-200 open:shadow-sm transition-all" data-testid={`sol-faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-5 text-sm md:text-base font-bold text-gray-900">
                    <span>{item.q}</span>
                    <span className="ml-4 text-blue-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <blockquote className="text-lg md:text-xl italic text-gray-700 leading-relaxed mb-5" data-testid="solution-quote">
            "{data.quote}"
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">
              {data.quoteAuthor.trim().charAt(0).toUpperCase()}
            </div>
            <div className="text-left">
              <p className="text-sm font-bold text-gray-900">{data.quoteAuthor}</p>
              <p className="text-xs text-gray-500">{data.quoteRole}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related roles */}
      {data.related && data.related.length > 0 && (
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-8">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Other roles we serve</p>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">See Forsyt for adjacent teams</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.related.map((s) => (
                <RelatedRoleCard key={s} roleSlug={s} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight">
            Ready to see Forsyt for {data.shortTitle}?
          </h2>
          <p className="text-base md:text-lg text-blue-100 mb-7 max-w-2xl mx-auto">
            Try the interactive sandbox, or book a 30-minute personalized demo with your pipeline data.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate(data.ctaLink)}
              className="inline-flex items-center px-7 py-3 text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 rounded-full shadow-lg transition-all"
            >
              {data.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-7 py-3 text-sm font-semibold border border-white/40 hover:border-white hover:bg-white/10 rounded-full transition-all"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      <HowForsytRollsIn />

      <Footer />
    </div>
  );
}
